import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Receipt, Loader2, ArrowLeft, Mail, CheckCircle, MailCheck, AlertCircle, Shield, MapPin, Gift, Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIndianStates } from "@/hooks/useIndianStates";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { z } from "zod";
import { Separator } from "@/components/ui/separator";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRateLimit } from "@/hooks/useRateLimit";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

// Enhanced password schema with stronger requirements
const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character");

const signupSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: passwordSchema,
  fullName: z.string().min(2, "Full name is required"),
  businessName: z.string().min(2, "Business name is required"),
  mobile: z.string().min(10, "Mobile number must be 10 digits").max(10, "Mobile number must be 10 digits").regex(/^[6-9][0-9]{9}$/, "Invalid mobile number"),
  stateCode: z.string().min(2, "Please select your state"),
  referralCode: z.string().optional(),
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Default to true for CSC operators
  const [showUnverifiedError, setShowUnverifiedError] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    email: "",
    password: "",
    fullName: "",
    businessName: "",
    mobile: "",
    stateCode: "",
    referralCode: "",
  });
  const { data: indianStates, isLoading: statesLoading } = useIndianStates();
  
  // Check for referral code in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref') || params.get('referral');
    if (refCode) {
      setSignupData(prev => ({ ...prev, referralCode: refCode.toUpperCase() }));
    }
  }, []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { signIn, signUp, resetPassword, resendVerificationEmail } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { checkRateLimit, recordLoginAttempt, rateLimitStatus, getLockoutMessage } = useRateLimit();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setShowUnverifiedError(false);

    const result = loginSchema.safeParse(loginData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    // Check rate limit before attempting login
    const rateLimitCheck = await checkRateLimit(loginData.email);
    if (rateLimitCheck.isLimited) {
      toast({
        title: "Too Many Attempts",
        description: getLockoutMessage() || "Please try again later.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const { error } = await signIn(loginData.email, loginData.password, rememberMe);
    setIsLoading(false);

    if (error) {
      // Record failed login attempt
      await recordLoginAttempt(loginData.email, false);
      
      // Check for email not confirmed error
      const errorMessage = error.message?.toLowerCase() || "";
      if (errorMessage.includes("email not confirmed") || errorMessage.includes("email_not_confirmed")) {
        setUnverifiedEmail(loginData.email);
        setShowUnverifiedError(true);
        return;
      }
      
      // Show remaining attempts warning
      const updatedStatus = await checkRateLimit(loginData.email);
      const attemptsWarning = updatedStatus.remainingAttempts <= 2 && updatedStatus.remainingAttempts > 0
        ? ` (${updatedStatus.remainingAttempts} attempts remaining)`
        : "";
      
      toast({
        title: "Login Failed",
        description: (error.message || "Invalid credentials. Please try again.") + attemptsWarning,
        variant: "destructive",
      });
    } else {
      // Record successful login
      await recordLoginAttempt(loginData.email, true);
      
      // Direct login - no OTP for normal daily usage
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });
      navigate("/dashboard");
    }
  };

  const handleResendFromLogin = async () => {
    setIsLoading(true);
    const { error } = await resendVerificationEmail(unverifiedEmail);
    setIsLoading(false);
    
    if (error) {
      toast({
        title: "Failed to resend",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Email Sent!",
        description: "We've resent the verification email. Please check your inbox.",
      });
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = signupSchema.safeParse(signupData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    const { error } = await signUp(
      signupData.email,
      signupData.password,
      signupData.fullName,
      signupData.businessName,
      signupData.mobile,
      signupData.stateCode,
      signupData.referralCode || undefined
    );
    setIsLoading(false);

    if (error) {
      let errorMessage = error.message;
      if (error.message.includes("already registered")) {
        errorMessage = "This email is already registered. Please login instead.";
      }
      toast({
        title: "Signup Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } else {
      // Show email verification message instead of navigating
      setVerificationEmail(signupData.email);
      setShowEmailVerification(true);
      toast({
        title: "Account Created!",
        description: "Please check your email to verify your account.",
      });
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = forgotPasswordSchema.safeParse({ email: forgotPasswordEmail });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    const { error } = await resetPassword(forgotPasswordEmail);
    setIsLoading(false);

    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email. Please try again.",
        variant: "destructive",
      });
    } else {
      setForgotPasswordSent(true);
      toast({
        title: "Email Sent!",
        description: "Check your inbox for the password reset link.",
      });
    }
  };


  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-4">
          <Link 
            to="/" 
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Home
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
              V+
            </div>
            <span className="text-lg font-semibold">VLE Plus</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 items-center justify-center p-4">{
        <Card className="w-full max-w-md">
          {showEmailVerification ? (
            // Email Verification Message
            <>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <MailCheck className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Verify Your Email</CardTitle>
                <CardDescription>
                  We've sent a verification link to <strong>{verificationEmail}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  Please check your inbox and click the verification link to activate your account. 
                  If you don't see the email, check your spam folder.
                </p>
                <Button
                  variant="default"
                  className="w-full"
                  disabled={isLoading || resendCooldown > 0}
                  onClick={async () => {
                    setIsLoading(true);
                    const { error } = await resendVerificationEmail(verificationEmail);
                    setIsLoading(false);
                    
                    if (error) {
                      toast({
                        title: "Failed to resend",
                        description: error.message || "Please try again later.",
                        variant: "destructive",
                      });
                    } else {
                      toast({
                        title: "Email Sent!",
                        description: "We've resent the verification email.",
                      });
                      // Start 60 second cooldown
                      setResendCooldown(60);
                      const interval = setInterval(() => {
                        setResendCooldown((prev) => {
                          if (prev <= 1) {
                            clearInterval(interval);
                            return 0;
                          }
                          return prev - 1;
                        });
                      }, 1000);
                    }
                  }}
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="mr-2 h-4 w-4" />
                  )}
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Verification Email"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowEmailVerification(false);
                    setVerificationEmail("");
                    setSignupData({ email: "", password: "", fullName: "", businessName: "", mobile: "", stateCode: "", referralCode: "" });
                  }}
                >
                  Back to Login
                </Button>
              </CardContent>
            </>
          ) : showForgotPassword ? (
            // Forgot Password Form
            forgotPasswordSent ? (
              <>
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <CardTitle>Check Your Email</CardTitle>
                  <CardDescription>
                    We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordSent(false);
                      setForgotPasswordEmail("");
                    }}
                  >
                    Back to Login
                  </Button>
                </CardContent>
              </>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <CardHeader>
                  <CardTitle>Forgot Password?</CardTitle>
                  <CardDescription>
                    Enter your email and we'll send you a reset link
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">{t('email')}</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      disabled={isLoading}
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive">{errors.email}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Mail className="mr-2 h-4 w-4" />
                    Send Reset Link
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setErrors({});
                    }}
                  >
                    Back to Login
                  </Button>
                </CardContent>
              </form>
            )
          ) : (
            // Login/Signup Tabs
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">{t('login')}</TabsTrigger>
                <TabsTrigger value="signup">{t('signup')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin}>
                  <CardHeader>
                    <CardTitle>{t('welcome_back')}</CardTitle>
                    <CardDescription>{t('login_subtitle')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Rate Limit Warning */}
                    {rateLimitStatus?.isLimited && (
                      <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
                        <Shield className="h-4 w-4" />
                        <AlertDescription className="ml-2">
                          <p className="font-medium">Account Temporarily Locked</p>
                          <p className="text-sm">{getLockoutMessage()}</p>
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    {/* Email Not Verified Alert */}
                    {showUnverifiedError && (
                      <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="ml-2">
                          <div className="space-y-2">
                            <p className="font-medium">Email not verified</p>
                            <p className="text-sm">
                              Please verify your email address before logging in. Check your inbox for a verification link.
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              disabled={isLoading || resendCooldown > 0}
                              onClick={handleResendFromLogin}
                            >
                              {isLoading ? (
                                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                              ) : (
                                <Mail className="mr-2 h-3 w-3" />
                              )}
                              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Verification Email"}
                            </Button>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="login-email">{t('email')}</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(e) =>
                          setLoginData({ ...loginData, email: e.target.value })
                        }
                        disabled={isLoading}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password">{t('password')}</Label>
                        <button
                          type="button"
                          onClick={() => {
                            setShowForgotPassword(true);
                            setErrors({});
                          }}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) =>
                          setLoginData({ ...loginData, password: e.target.value })
                        }
                        disabled={isLoading}
                      />
                      {errors.password && (
                        <p className="text-sm text-destructive">{errors.password}</p>
                      )}
                    </div>
                    
                    {/* Remember Me Checkbox - 30 days */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked === true)}
                        disabled={isLoading}
                      />
                      <Label 
                        htmlFor="remember-me" 
                        className="text-sm font-normal text-muted-foreground cursor-pointer"
                      >
                        Remember this device for 30 days
                      </Label>
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('login')}
                    </Button>
                  </CardContent>
                </form>
              </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup}>
                <CardHeader>
                  <CardTitle>{t('create_account')}</CardTitle>
                  <CardDescription>{t('signup_subtitle')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">

                  <div className="space-y-2">
                    <Label htmlFor="signup-name">{t('full_name')}</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={signupData.fullName}
                      onChange={(e) =>
                        setSignupData({ ...signupData, fullName: e.target.value })
                      }
                      disabled={isLoading}
                    />
                    {errors.fullName && (
                      <p className="text-sm text-destructive">{errors.fullName}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-business">{t('business_name')}</Label>
                    <Input
                      id="signup-business"
                      type="text"
                      placeholder="My CSC Center"
                      value={signupData.businessName}
                      onChange={(e) =>
                        setSignupData({ ...signupData, businessName: e.target.value })
                      }
                      disabled={isLoading}
                    />
                    {errors.businessName && (
                      <p className="text-sm text-destructive">{errors.businessName}</p>
                    )}
                  </div>
                  
                  {/* Mobile Number - Required */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-mobile" className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Mobile Number *
                    </Label>
                    <div className="flex">
                      <div className="flex items-center justify-center px-3 border border-r-0 rounded-l-md bg-muted text-muted-foreground text-sm">
                        +91
                      </div>
                      <Input
                        id="signup-mobile"
                        type="tel"
                        placeholder="9876543210"
                        value={signupData.mobile}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setSignupData({ ...signupData, mobile: value });
                        }}
                        disabled={isLoading}
                        className="rounded-l-none"
                        maxLength={10}
                      />
                    </div>
                    {errors.mobile && (
                      <p className="text-sm text-destructive">{errors.mobile}</p>
                    )}
                  </div>
                  
                  {/* State Selection - Required */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-state" className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      State *
                    </Label>
                    <Select
                      value={signupData.stateCode}
                      onValueChange={(value) =>
                        setSignupData({ ...signupData, stateCode: value })
                      }
                      disabled={isLoading || statesLoading}
                    >
                      <SelectTrigger id="signup-state">
                        <SelectValue placeholder="Select your state" />
                      </SelectTrigger>
                      <SelectContent>
                        {indianStates?.map((state) => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.name} {state.name_hi ? `(${state.name_hi})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.stateCode && (
                      <p className="text-sm text-destructive">{errors.stateCode}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      ⚠️ State cannot be changed after signup
                    </p>
                  </div>
                  
                  {/* Referral Code - Optional */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-referral" className="flex items-center gap-1">
                      <Gift className="h-3 w-3" />
                      Referral Code (Optional)
                    </Label>
                    <Input
                      id="signup-referral"
                      type="text"
                      placeholder="Enter referral code"
                      value={signupData.referralCode}
                      onChange={(e) =>
                        setSignupData({ ...signupData, referralCode: e.target.value.toUpperCase() })
                      }
                      disabled={isLoading}
                      className="uppercase"
                    />
                    {signupData.referralCode && (
                      <p className="text-xs text-green-600">
                        ✓ Referral code applied
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">{t('email')}</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signupData.email}
                      onChange={(e) =>
                        setSignupData({ ...signupData, email: e.target.value })
                      }
                      disabled={isLoading}
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive">{errors.email}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">{t('password')}</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={signupData.password}
                      onChange={(e) =>
                        setSignupData({ ...signupData, password: e.target.value })
                      }
                      disabled={isLoading}
                    />
                    {errors.password && (
                      <p className="text-sm text-destructive">{errors.password}</p>
                    )}
                    <PasswordStrengthIndicator password={signupData.password} />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('signup')}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>
          </Tabs>
          )}
        </Card>}
      </main>
    </div>
  );
}
